import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { intakeToSections, sectionLabel, fieldLabel, humanValue } from "@/lib/intakeView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, MicOff, Send, Camera, Ruler, CheckCircle2, HelpCircle, Loader2, ArrowLeft,
  ClipboardList, Sparkles, ImageIcon, Bot, User, AlertTriangle, X, MessageSquare,
} from "lucide-react";

const SELECTION_CATEGORIES = ["Shower tile", "Floor tile", "Vanity", "Countertop", "Sink", "Faucets", "Shower fixtures", "Toilet", "Shower glass", "Lighting", "Mirror", "Paint"];
const PHOTO_CATEGORIES = ["before", "during", "issues", "walkthrough", "after"];

function useSpeech({ onInterim, onFinal }) {
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const shouldListenRef = useRef(false);
  const finalRef = useRef("");
  const interimRef = useRef("");
  const supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const start = useCallback(() => {
    if (!supported) { toast.error("Voice input isn't supported in this browser. Please type your answer instead."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = true;
    r.continuous = true;
    finalRef.current = "";
    interimRef.current = "";
    shouldListenRef.current = true;

    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalRef.current += t + " ";
        else interim += t;
      }
      interimRef.current = interim;
      onInterim && onInterim((finalRef.current + interim).trim());
    };
    r.onerror = (ev) => {
      // ignore benign auto-stops; keep session alive
      if (ev.error === "no-speech" || ev.error === "aborted" || ev.error === "audio-capture") return;
      if (ev.error === "not-allowed") { toast.error("Microphone permission is blocked. Enable mic access for this site."); shouldListenRef.current = false; }
    };
    r.onend = () => {
      // Chrome auto-ends after a pause/timeout; restart while the user still wants to talk
      if (shouldListenRef.current) {
        try { r.start(); } catch (e) { /* already starting */ }
      } else {
        setListening(false);
      }
    };
    recogRef.current = r;
    try { r.start(); setListening(true); } catch (e) { /* noop */ }
  }, [onInterim, supported]);

  const stop = useCallback(() => {
    shouldListenRef.current = false;
    try { recogRef.current?.stop(); } catch (e) { /* noop */ }
    setListening(false);
    const text = (finalRef.current + " " + interimRef.current).trim();
    finalRef.current = "";
    interimRef.current = "";
    if (text) onFinal(text);
  }, [onFinal]);

  return { listening, start, stop, supported };
}

export default function VisitWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [intake, setIntake] = useState({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [nextQuestion, setNextQuestion] = useState("");
  const [flashKeys, setFlashKeys] = useState([]);
  const scrollRef = useRef(null);

  // dialogs
  const [photoOpen, setPhotoOpen] = useState(false);
  const [measureOpen, setMeasureOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [editField, setEditField] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    setProject(data);
    setIntake(data.intake || {});
    const convo = (data.conversation || []).map((m) => ({ role: m.role === "ai" ? "ai" : "user", content: m.content }));
    if (convo.length === 0) {
      setMessages([{ role: "ai", content: `Hi! I'm your Amaa estimator assistant. Tell me what the customer wants to change about this bathroom — just talk naturally and I'll capture everything.` }]);
      setNextQuestion("Tell me what the customer would like to change about this bathroom.");
    } else {
      setMessages(convo);
      const last = [...(data.conversation || [])].reverse().find((m) => m.role === "ai");
      if (last) { setNextQuestion(last.next_question || ""); setSuggested(last.suggested_answers || []); }
    }
  };

  useEffect(() => { load(); }, [projectId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, thinking]);

  const sendText = async (text) => {
    if (!text.trim() || thinking) return;
    setInput("");
    setTextOpen(false);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setThinking(true);
    setSuggested([]);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/chat`, { text });
      setMessages((m) => [...m, { role: "ai", content: data.reply }]);
      setNextQuestion(data.next_question || "");
      setSuggested(data.suggested_answers || []);
      setIntake(data.intake || {});
      const changed = flatKeys(data.updates || {});
      if (changed.length) { setFlashKeys(changed); setTimeout(() => setFlashKeys([]), 1300); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "ai", content: "Sorry, I had trouble processing that. Please try again." }]);
    } finally {
      setThinking(false);
    }
  };

  const { listening, start, stop, supported } = useSpeech({
    onInterim: (text) => setInput(text),
    onFinal: (text) => sendText(text),
  });

  const whatNext = async () => {
    setThinking(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/ai/next`);
      setMessages((m) => [...m, { role: "ai", content: data.instruction }]);
      setNextQuestion(data.instruction);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setThinking(false); }
  };

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>;
  }

  const sections = intakeToSections(intake);
  const photoCount = (project.photos || []).length;
  const measureCount = (project.measurements || []).length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* top bar */}
      <div className="sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-950/90 backdrop-blur-xl">
        <button data-testid="visit-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <div className="text-center">
          <div className="font-display font-bold text-lg leading-none">{project.customer_name}</div>
          <div className="text-[11px] text-slate-500">Full Bathroom Remodel · {project.address}</div>
        </div>
        <div className="text-xs text-orange-400 font-semibold uppercase tracking-wider hidden sm:block">Site Visit</div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 pb-28 max-w-[1600px] mx-auto w-full">
        {/* LEFT: AI assistant */}
        <div className="lg:col-span-7 flex flex-col rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden min-h-[60vh]">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2 bg-slate-900/80">
            <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center"><Bot className="w-5 h-5 text-orange-400" /></div>
            <div><div className="font-display font-bold">Amaa AI Estimator</div><div className="text-[11px] text-slate-500">Speak or type — I'll record everything</div></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-conversation">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-sky-500/20" : "bg-orange-500/20"}`}>
                  {m.role === "user" ? <User className="w-4 h-4 text-sky-400" /> : <Bot className="w-4 h-4 text-orange-400" />}
                </div>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-sky-500/15 text-sky-100 rounded-tr-sm" : "bg-slate-800 text-slate-100 rounded-tl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center"><Bot className="w-4 h-4 text-orange-400" /></div>
                <div className="bg-slate-800 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                  {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                </div>
              </div>
            )}
          </div>

          {/* suggested answers */}
          {suggested.length > 0 && !thinking && (
            <div className="px-5 pb-2 flex flex-wrap gap-2">
              {suggested.map((s, i) => (
                <button key={i} data-testid={`suggested-answer-${i}`} onClick={() => sendText(s)}
                  className="text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-orange-500/50 hover:text-orange-300 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* mic + input */}
          {listening && (
            <div className="px-5 pt-3 flex items-center gap-2 text-rose-300 text-sm" data-testid="listening-indicator">
              <span className="flex items-end gap-0.5 h-4">
                {[0, 1, 2, 3].map((i) => <span key={i} className="w-0.5 bg-rose-400 wave-bar rounded" style={{ animationDelay: `${i * 0.12}s` }} />)}
              </span>
              Listening… keep talking, then <span className="font-semibold">tap the mic to send</span>.
            </div>
          )}
          <div className="p-4 border-t border-slate-800 flex items-center gap-3">
            <button
              data-testid="ai-mic-button"
              onClick={listening ? stop : start}
              className={`relative w-14 h-14 rounded-full flex items-center justify-center shrink-0 transition-colors ${listening ? "bg-rose-500 mic-listening" : "bg-orange-500 hover:bg-orange-600"}`}
            >
              {listening ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-slate-950" />}
            </button>
            <div className="flex-1 flex items-center gap-2">
              <Input
                data-testid="ai-text-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendText(input)}
                placeholder={listening ? "Listening… tap the mic when you're done" : "Type your answer, or tap the mic to talk…"}
                className="bg-slate-950 border-slate-700 h-12"
              />
              <Button data-testid="ai-send-button" onClick={() => (listening ? stop() : sendText(input))} disabled={thinking || (!listening && !input.trim())} className="h-12 w-12 bg-orange-500 hover:bg-orange-600 text-slate-950 shrink-0">
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT: smart panel */}
        <div className="lg:col-span-5 flex flex-col rounded-2xl bg-slate-900/60 border border-slate-800 shadow-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-orange-400" />
            <div className="font-display font-bold">Project Information</div>
            <Sparkles className="w-4 h-4 text-orange-400/60 ml-auto" />
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[calc(100vh-260px)]">
            {sections.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-10">
                <Bot className="w-8 h-8 mx-auto mb-3 text-slate-700" />
                As you talk, the details you mention will appear here automatically.
              </div>
            )}
            {sections.map((sec) => (
              <div key={sec.key} className="rounded-xl bg-slate-900 border border-slate-800 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-2">{sectionLabel(sec.key)}</div>
                <div className="space-y-1.5">
                  {sec.fields.map(([k, v]) => {
                    const fk = `${sec.key}.${k}`;
                    return (
                      <button
                        key={k}
                        data-testid={`smart-field-${fk}`}
                        onClick={() => setEditField({ section: sec.key, key: k, value: v })}
                        className={`w-full flex items-center justify-between text-sm rounded-lg px-2 py-1.5 hover:bg-slate-800 transition-colors ${flashKeys.includes(fk) ? "flash-fill" : ""}`}
                      >
                        <span className="text-slate-400">{fieldLabel(k)}</span>
                        <span className="font-medium text-slate-100 font-mono-data text-right">{humanValue(v)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* measurements */}
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Measurements</div>
                <span className="text-xs text-slate-600">{measureCount}</span>
              </div>
              {measureCount === 0 ? <div className="text-xs text-slate-600">No measurements yet</div> : (
                <div className="space-y-1.5">
                  {project.measurements.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{m.label}</span>
                      <span className="font-mono-data">{m.value} {m.unit}
                        {m.source === "calculated" && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300">CALC</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* photos */}
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Photos</div>
                <span className="text-xs text-slate-600">{photoCount}</span>
              </div>
              {photoCount === 0 ? <div className="text-xs text-slate-600">No photos yet</div> : (
                <div className="grid grid-cols-4 gap-1.5">
                  {project.photos.map((p) => (
                    <img key={p.id} src={fileUrl(p.storage_path)} alt={p.category} className="w-full h-14 object-cover rounded-md border border-slate-700" />
                  ))}
                </div>
              )}
            </div>

            {/* selections */}
            <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400">Customer Selections</div>
                <button data-testid="add-selection-button" onClick={() => setSelectionOpen(true)} className="text-xs text-orange-400 hover:text-orange-300">+ Add</button>
              </div>
              {(project.selections || []).length === 0 ? <div className="text-xs text-slate-600">None recorded</div> : (
                <div className="space-y-1.5">
                  {project.selections.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{s.category}</span>
                      <SelectionStatus status={s.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PERMANENT ACTION BAR */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 p-2 rounded-full bg-slate-950/95 backdrop-blur-xl border border-orange-500/30 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
        <ActionBtn testid="action-talk" icon={Mic} label="Talk" active={listening} onClick={listening ? stop : start} />
        <ActionBtn testid="action-type" icon={MessageSquare} label="Type" onClick={() => setTextOpen(true)} />
        <ActionBtn testid="action-photo" icon={Camera} label="Photo" onClick={() => setPhotoOpen(true)} />
        <ActionBtn testid="action-measure" icon={Ruler} label="Measure" onClick={() => setMeasureOpen(true)} />
        <ActionBtn testid="action-confirm" icon={CheckCircle2} label="Confirm" onClick={() => setVerifyOpen(true)} />
        <button
          data-testid="what-next-button"
          onClick={whatNext}
          className="ml-1 flex items-center gap-1.5 px-4 h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold text-sm hover:scale-105 transition-transform"
        >
          <HelpCircle className="w-4 h-4" /> <span className="hidden sm:inline">What Next?</span>
        </button>
      </div>

      {/* dialogs */}
      <TextDialog open={textOpen} onClose={() => setTextOpen(false)} onSend={sendText} nextQuestion={nextQuestion} />
      <PhotoDialog open={photoOpen} onClose={() => setPhotoOpen(false)} projectId={projectId} onDone={load} />
      <MeasureDialog open={measureOpen} onClose={() => setMeasureOpen(false)} projectId={projectId} onDone={load} />
      <SelectionDialog open={selectionOpen} onClose={() => setSelectionOpen(false)} projectId={projectId} onDone={load} />
      <EditFieldDialog field={editField} onClose={() => setEditField(null)} projectId={projectId} onSaved={(nk) => { setIntake(nk); setEditField(null); }} />
      <VerifyDialog open={verifyOpen} onClose={() => setVerifyOpen(false)} projectId={projectId} onSent={() => navigate("/dashboard")} />
    </div>
  );
}

function flatKeys(obj, prefix = "") {
  let keys = [];
  for (const [k, v] of Object.entries(obj)) {
    const nk = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) keys = keys.concat(flatKeys(v, nk));
    else keys.push(nk);
  }
  return keys;
}

function ActionBtn({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex flex-col items-center justify-center w-14 h-11 rounded-full transition-colors ${active ? "bg-rose-500 text-white" : "text-slate-300 hover:bg-slate-800"}`}>
      <Icon className="w-4.5 h-4.5" />
      <span className="text-[9px] mt-0.5">{label}</span>
    </button>
  );
}

function SelectionStatus({ status }) {
  const map = { selected: ["bg-emerald-500/20 text-emerald-300", "Selected"], preference: ["bg-amber-500/20 text-amber-300", "Preference"], undecided: ["bg-slate-600/30 text-slate-400", "Undecided"] };
  const [cls, label] = map[status] || map.undecided;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function TextDialog({ open, onClose, onSend, nextQuestion }) {
  const [val, setVal] = useState("");
  useEffect(() => { if (open) setVal(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Type an answer</DialogTitle></DialogHeader>
        {nextQuestion && <div className="text-sm text-slate-400 bg-slate-800/50 rounded-lg p-3">{nextQuestion}</div>}
        <Textarea data-testid="type-dialog-input" value={val} onChange={(e) => setVal(e.target.value)} rows={4} placeholder="Type here…" className="bg-slate-950 border-slate-700" />
        <DialogFooter>
          <Button data-testid="type-dialog-send" onClick={() => { onSend(val); }} disabled={!val.trim()} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Send to AI</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoDialog({ open, onClose, projectId, onDone }) {
  const [category, setCategory] = useState("before");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("note", note);
      await api.post(`/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      setNote("");
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Take / Upload Photo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-400 text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="photo-category-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {PHOTO_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Water staining near tub" className="bg-slate-950 border-slate-700 mt-1" />
          </div>
          <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          <Button data-testid="photo-capture-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5 mr-2" /> Capture / Choose Photo</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MeasureDialog({ open, onClose, projectId, onDone }) {
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("in");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setLabel(""); setValue(""); } }, [open]);

  const save = async () => {
    if (!label.trim() || !value.trim()) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/measurements`, { label, value, unit, source: "field" });
      toast.success("Measurement recorded");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Add Measurement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">What are you measuring?</Label>
            <Input data-testid="measure-label-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Shower width" className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label className="text-slate-400 text-xs">Value</Label>
              <Input data-testid="measure-value-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder='e.g. 60 or 9 ft 2 in' className="bg-slate-950 border-slate-700 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {["in", "ft", "cm", "sq ft"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="measure-save-button" onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectionDialog({ open, onClose, projectId, onDone }) {
  const [category, setCategory] = useState(SELECTION_CATEGORIES[0]);
  const [status, setStatus] = useState("undecided");
  const [detail, setDetail] = useState("");
  const save = async () => {
    try {
      await api.post(`/projects/${projectId}/selections`, { category, status, detail });
      toast.success("Selection updated");
      onDone(); onClose(); setDetail("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Customer Selection</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-slate-400 text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="selection-category-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-64">{SELECTION_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-slate-400 text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="selection-status-select" className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="selected">Selected</SelectItem>
                <SelectItem value="preference">Preference Known / Not Final</SelectItem>
                <SelectItem value="undecided">Undecided</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="text-slate-400 text-xs">Detail (optional)</Label>
            <Input value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="e.g. 12x24 matte porcelain" className="bg-slate-950 border-slate-700 mt-1" /></div>
        </div>
        <DialogFooter><Button data-testid="selection-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditFieldDialog({ field, onClose, projectId, onSaved }) {
  const [val, setVal] = useState("");
  useEffect(() => { if (field) setVal(typeof field.value === "boolean" ? String(field.value) : String(field.value)); }, [field]);
  if (!field) return null;
  const save = async () => {
    let parsed = val;
    if (val === "true") parsed = true; else if (val === "false") parsed = false;
    else if (/^-?\d+(\.\d+)?$/.test(val)) parsed = Number(val);
    try {
      const { data } = await api.patch(`/projects/${projectId}/intake`, { updates: { [field.section]: { [field.key]: parsed } } });
      toast.success("Updated");
      onSaved(data.intake);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!field} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle className="font-display">Edit {fieldLabel(field.key)}</DialogTitle></DialogHeader>
        <Input data-testid="edit-field-input" value={val} onChange={(e) => setVal(e.target.value)} className="bg-slate-950 border-slate-700" />
        <DialogFooter><Button data-testid="edit-field-save" onClick={save} className="bg-orange-500 hover:bg-orange-600 text-slate-950">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ open, onClose, projectId, onSent }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setData(null); setCompleteness(null);
    Promise.all([api.get(`/projects/${projectId}/verify`), api.get(`/projects/${projectId}/completeness`)])
      .then(([v, c]) => { setData(v.data); setCompleteness(c.data); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/projects/${projectId}/submit`);
      toast.success("Sent to office! Pricing is on the way.");
      onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Verify & Send to Office</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-3 text-slate-400"><Loader2 className="w-7 h-7 text-orange-500 animate-spin" /> Reviewing everything…</div>
        ) : (
          <div className="space-y-4">
            {data && (
              <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-2">Plain-English Summary</div>
                <p className="text-sm leading-relaxed text-slate-200" data-testid="verify-summary">{data.summary}</p>
              </div>
            )}
            {data?.office_attention?.length > 0 && (
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Office Attention</div>
                <ul className="text-sm space-y-1 text-amber-100">{data.office_attention.map((o, i) => <li key={i}>• {o}</li>)}</ul>
              </div>
            )}
            {completeness?.missing?.length > 0 && (
              <div className="rounded-xl bg-slate-800/50 border border-slate-700 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-sky-400 mb-2">Still Missing (optional)</div>
                <ul className="text-sm space-y-1 text-slate-300">{completeness.missing.map((m, i) => <li key={i}>• {m.label}</li>)}</ul>
                <p className="text-[11px] text-slate-500 mt-2">You can still send — the office will verify these.</p>
              </div>
            )}
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300 hover:bg-slate-800">Edit / Add More</Button>
          <Button data-testid="approve-send-button" onClick={send} disabled={sending || loading} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> APPROVE & SEND</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
